import type {PaginatedResponse} from '@myparcel/sdk';
import type {Status} from '@myparcel-frontend/sdk';
import type {ValueOf} from '@myparcel-frontend/types';

export interface PaginationWrapper<T> {
  data: T[];
  page: number;
  size: number;
  results: number;
  status: ValueOf<typeof Status>;
}

/**
 * Helper to conveniently include pagination handlers for a list of items.
 * You can use this to reactively fetch a list of items from the API, and control the pagination.
 *
 * @param fn - The function to call to fetch the items.
 * @param params - The parameters to pass to the fetcher function.
 * @param accessor - A function to access the items from the response, usually a property on the response.
 * @param path (optional) - The path params used in the fetcher function, for example {id: 1} for /items/:id.
 * @returns The object as specified in PaginationWrapper above. Contains some refs and a fetchPage function.
 */
const paginationWrapper = async <T>(
  fn: (params: Record<string, unknown>) => Promise<PaginatedResponse<T[]>>,
  params: {
    page?: number;
    size?: number;
  } & Record<string, unknown>,
  accessor: (item: PaginatedResponse<T[]>) => T[],
  path?: Record<string, unknown>,
): Promise<PaginationWrapper<T>> => {
  const response = await fn({
    parameters: params,
    ...(path && {path}),
  });
  const data = accessor(response);
  const page = response.page ?? params.page ?? 1;
  const size = response.size ?? params.size ?? 10;
  const {results} = response;

  return {
    data,
    page,
    size,
    results,
  };
};

export default paginationWrapper;
